import { LoadingButton } from '@mui/lab';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ColorizeRoundedIcon from '@mui/icons-material/ColorizeRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import UploadAvatar from 'src/components/upload/UploadAvatar';
import ChatLoader from 'src/components/loading/chat-loader.component';
import StatusSwitch from 'src/components/switch/status-switch.component';
import ColorPopover from '../branding/branding-color-popover';

const defaultWidgetSettings = {
  widget_name: 'Support Chat',
  profile_image_url: '',
  primary_color: '#534ab7',
  secondary_color: '#f5f4fd',
  position: 'bottom_right',
  greeting_message: 'Hi, how can we help?',
  offline_message: 'We are offline now. Leave your message and we will reply soon.',
  language: 'english',
  is_enabled: false,
};

const defaultBrandingSettings = {
  widget_name: defaultWidgetSettings.widget_name,
  profile_image_url: defaultWidgetSettings.profile_image_url,
  primary_color: defaultWidgetSettings.primary_color,
  secondary_color: defaultWidgetSettings.secondary_color,
  position: defaultWidgetSettings.position,
};

export default function WidgetSetup() {
  const { t: translate } = useTranslation();
  const [loader, setLoader] = useState(true);
  const [saveLoader, setSaveLoader] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [selectedColor, setSelectedColor] = useState('primary');
  const [activeTab, setActiveTab] = useState('branding');
  const [settings, setSettings] = useState(defaultWidgetSettings);
  const { showToast } = useToaster();
  const enableInFlightRef = useRef(false);
  const canManageWidget = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.WIDGET_MANAGE);
  const canUseWidgetBranding = CommonHelper.hasWhiteLabelOrCustomDomianAccess();
  const isWidgetEnabled = settings.is_enabled;
  const setupDisabled = !isWidgetEnabled;
  const brandingDisabled = setupDisabled || !canUseWidgetBranding;
  const disabledSectionSx = {
    opacity: setupDisabled ? 0.45 : 1,
    transition: 'opacity 180ms ease',
  };
  const sectionPaperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 10px 30px rgba(33, 43, 54, 0.05)',
    transition: 'box-shadow 180ms ease, border-color 180ms ease',
    '&:hover': {
      borderColor: 'primary.light',
      boxShadow: '0 14px 36px rgba(33, 43, 54, 0.08)',
    },
  };
  const embedScript = settings.widget_key
    ? `<script src="${process.env.REACT_APP_REDIRECT_URL}/embed/chat-widget.js" data-widget-key="${
        settings.widget_key
      }"  data-api-base="${(process.env.REACT_APP_API_URL || `${window.location.origin}/api`).replace(
        /\/+$/,
        ''
      )}/widget/v1" data-ws-base="${(process.env.REACT_APP_WEBSOCKET_URL || `${window.location.origin}/api`).replace(
        /\/+$/,
        ''
      )}" async></script>`
    : '';
  const widgetControlExample = `window.Brand2Chat_API = window.Brand2Chat_API || {};
window.Brand2Chat_API.onLoad = function () {
  window.Brand2Chat_API.showWidget();
};

// Hide or display the widget later when needed:
// window.Brand2Chat_API.hideWidget();
// window.Brand2Chat_API.showWidget();
// const isHidden = window.Brand2Chat_API.isChatHidden();`;

  const fetchWidgetSettings = useCallback(() => {
    if (!canManageWidget) {
      setLoader(false);
      return;
    }
    setLoader(true);
    HttpServices.Get({
      url: userModuleURL.chatWidgetSettings,
      successCallback: (data) => {
        setSettings({
          ...defaultWidgetSettings,
          ...data,
          is_enabled: Boolean(data?.widget_key) && Boolean(data?.is_enabled),
        });
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        message && showToast(message, 'e');
      },
    });
  }, [canManageWidget, showToast]);

  useEffect(() => {
    fetchWidgetSettings();
  }, [fetchWidgetSettings]);

  const getWidgetSaveBody = (overrides = {}) => ({
    ...settings,
    ...(!canUseWidgetBranding ? defaultBrandingSettings : {}),
    ...overrides,
  });

  const handleInputChange = (event) => {
    const { name, checked, type } = event.target;
    const { value } = event.target;
    setSettings((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveWidgetSettings = () => {
    setSaveLoader(true);
    HttpServices.Post({
      url: userModuleURL.chatWidgetSettings,
      body: getWidgetSaveBody(),
      successCallback: (data) => {
        setSettings((prev) => ({ ...defaultWidgetSettings, ...prev, ...data }));
        setSaveLoader(false);
        showToast(
          translate('widget_setup_page.saved_successfully', {
            defaultValue: 'Chat widget settings saved',
          }),
          's'
        );
      },
      failureCallback: (message) => {
        setSaveLoader(false);
        message && showToast(message, 'e');
      },
    });
  };

  const handleWidgetEnabledChange = (event) => {
    if (enableInFlightRef.current) {
      return;
    }

    const isEnabled = event.target.checked;
    const previousEnabled = Boolean(settings.is_enabled);

    setSettings((prev) => ({ ...prev, is_enabled: isEnabled }));
    enableInFlightRef.current = true;

    HttpServices.Post({
      url: userModuleURL.chatWidgetSettings,
      body: getWidgetSaveBody({ is_enabled: isEnabled }),
      successCallback: (data) => {
        setSettings((prev) => ({
          ...defaultWidgetSettings,
          ...prev,
          ...data,
          is_enabled: data?.is_enabled !== undefined ? Boolean(data.is_enabled) : isEnabled,
        }));
        enableInFlightRef.current = false;
        showToast(
          translate('widget_setup_page.saved_successfully', {
            defaultValue: 'Chat widget settings saved',
          }),
          's'
        );
      },
      failureCallback: (message) => {
        setSettings((prev) => ({ ...prev, is_enabled: previousEnabled }));
        enableInFlightRef.current = false;
        message && showToast(message, 'e');
      },
    });
  };

  const uploadProfileImage = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (!CommonHelper.SupportedImageFormat.some((format) => format.includes(file.type))) {
      showToast(
        translate('widget_setup_page.supported_image_required', {
          defaultValue: 'Please select a supported image file',
        }),
        'e'
      );
      return;
    }
    if (file.size >= 10 * 1024 * 1024) {
      showToast(
        translate('widget_setup_page.profile_picture_size_limit', {
          defaultValue: 'Profile picture must be smaller than 10 MB',
        }),
        'e'
      );
      return;
    }

    const formData = new FormData();
    formData.append('fileupload', file);
    setImageUploadProgress(1);
    axios
      .post(imageModeuleUrl.uploadMedia, formData, {
        headers: HttpServices.GetRequestHeader('form', true),
        onUploadProgress: ({ loaded, total }) => {
          const progress = total ? Math.round((100 * loaded) / total) : 1;
          setImageUploadProgress(Math.min(progress, 90));
        },
      })
      .then((response) => {
        const mediaURL = response.data?.data?.media_data?.[0]?.media_url;
        if (response.status === 200 && response.data?.http_code === 200 && mediaURL) {
          setSettings((prev) => ({ ...prev, profile_image_url: mediaURL }));
          showToast(
            translate('widget_setup_page.profile_picture_uploaded', { defaultValue: 'Profile picture uploaded' }),
            's'
          );
        } else {
          showToast(
            translate('widget_setup_page.profile_picture_upload_failed', {
              defaultValue: 'Unable to upload profile picture',
            }),
            'e'
          );
        }
      })
      .catch(() =>
        showToast(
          translate('widget_setup_page.profile_picture_upload_failed', {
            defaultValue: 'Unable to upload profile picture',
          }),
          'e'
        )
      )
      .finally(() => setImageUploadProgress(0));
  };

  const formatHexColor = (value) => {
    const cleaned = String(value || '')
      .replaceAll('#', '')
      .replace(/[^0-9a-fA-F]/g, '')
      .slice(0, 6);
    return cleaned ? `#${cleaned}` : '';
  };

  const handleColorInput = (event) => {
    const { name, value } = event.target;
    setSettings((prev) => ({ ...prev, [name]: formatHexColor(value) }));
  };

  const openColorPicker = (event, type) => {
    setSelectedColor(type);
    setColorAnchor(event.currentTarget);
  };

  const handlePickedColor = (color, type) => {
    const field = type === 'primary' ? 'primary_color' : 'secondary_color';
    setSettings((prev) => ({ ...prev, [field]: formatHexColor(color.hex) }));
  };

  const copyTextToClipboard = (text, successMessage) => {
    if (!text) {
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    showToast(successMessage, 's');
  };

  const copyEmbedScript = () => {
    if (!embedScript) {
      showToast(
        translate('widget_setup_page.save_to_generate_script', {
          defaultValue: 'Save widget setup to generate the script',
        }),
        'e'
      );
      return;
    }
    copyTextToClipboard(
      embedScript,
      translate('widget_setup_page.script_copied', { defaultValue: 'Widget script copied' })
    );
  };

  const copyWidgetControlExample = () => {
    copyTextToClipboard(
      widgetControlExample,
      translate('widget_setup_page.widget_api_script_copied', { defaultValue: 'Widget API script copied' })
    );
  };

  return (
    <>
      <Helmet>
        <title>{translate('widget_setup_page.title', { defaultValue: 'Widget Setup' })}</title>
      </Helmet>
      <Box sx={{ height: 'calc(100vh - 70px)', minHeight: 0, overflow: 'hidden', pb: 2 }}>
        <Stack
          spacing={2.5}
          sx={{
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            px: 0.75,
            mx: 'auto',
            pb: { xs: 10, md: 6 },
            scrollbarGutter: 'stable both-edges',
          }}
        >
          <Paper
            variant="outlined"
            sx={(theme) => ({
              position: 'relative',
              overflow: 'hidden',
              p: { xs: 2, sm: 2.75 },
              borderRadius: 3,
              flexShrink: 0,
              borderColor: alpha(theme.palette.primary.main, 0.2),
              bgcolor: 'background.paper',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
            })}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Stack direction="row" spacing={1.75} alignItems="center">
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: 2.25,
                    color: 'primary.main',
                    bgcolor: 'background.paper',
                    boxShadow: '0 8px 22px rgba(83, 74, 183, 0.16)',
                  }}
                >
                  <ChatBubbleOutlineRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {translate('widget_setup_page.title', { defaultValue: 'Widget Setup' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {translate('widget_setup_page.subtitle', {
                      defaultValue: 'Configure the customer-facing chat widget for this workspace.',
                    })}
                  </Typography>
                </Box>
              </Stack>
              {canManageWidget && (
                <StatusSwitch
                  checked={isWidgetEnabled}
                  disabled={loader}
                  name="is_enabled"
                  onChange={handleWidgetEnabledChange}
                  inputProps={{
                    'aria-label': translate('widget_setup_page.enable_widget', {
                      defaultValue: 'Enable chat widget',
                    }),
                  }}
                />
              )}
            </Stack>
          </Paper>

          {loader ? (
            <ChatLoader
              label={translate('widget_setup_page.loading', { defaultValue: 'Loading widget setup' })}
              description={translate('widget_setup_page.loading_description', {
                defaultValue: 'Preparing your chat experience',
              })}
            />
          ) : !canManageWidget ? (
            <Typography variant="body2" color="text.secondary">
              {translate('widget_setup_page.permission_note', {
                defaultValue: 'Widget configuration is limited to workspace admins.',
              })}
            </Typography>
          ) : (
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, flexShrink: 0 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_event, value) => setActiveTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab
                    value="branding"
                    label={translate('widget_setup_page.widget_branding', {
                      defaultValue: 'Widget branding',
                    })}
                  />
                  <Tab
                    value="setup"
                    label={translate('widget_setup_page.widget_settings', {
                      defaultValue: 'Widget settings',
                    })}
                  />
                </Tabs>
              </Paper>

              {activeTab === 'branding' && (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    !canUseWidgetBranding
                      ? translate('widget_setup_page.custom_domain_required', {
                          defaultValue: 'Enable custom domain to customize the widget',
                        })
                      : ''
                  }
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      ...sectionPaperSx,
                      ...disabledSectionSx,
                      ...(!canUseWidgetBranding && { opacity: 0.55 }),
                    }}
                  >
                    <Stack spacing={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">
                          {translate('widget_setup_page.widget_branding', { defaultValue: 'Widget branding' })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {translate('widget_setup_page.widget_branding_help', {
                            defaultValue: 'This picture and these colors are used only by the chat widget.',
                          })}
                        </Typography>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 'fit-content',
                            mx: 'auto',
                          }}
                        >
                          <UploadAvatar
                            accept={{ 'image/*': [] }}
                            file={settings.profile_image_url || null}
                            onDrop={uploadProfileImage}
                            disabled={brandingDisabled || imageUploadProgress > 0}
                            handleDeleteProfiles={() => setSettings((prev) => ({ ...prev, profile_image_url: '' }))}
                            helperText={
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                {translate('widget_setup_page.image_help', {
                                  defaultValue: 'Allowed JPEG, PNG, GIF or WebP. Max size 10 MB.',
                                })}
                              </Typography>
                            }
                          />
                        </Box>
                        {imageUploadProgress > 0 && (
                          <LinearProgress variant="determinate" value={imageUploadProgress} sx={{ mt: 2 }} />
                        )}
                      </Box>

                      <Box>
                        <Typography variant="h6" sx={{ mb: 0.5 }}>
                          {translate('widget_setup_page.widget_appearance', { defaultValue: 'Widget appearance' })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {translate('widget_setup_page.widget_appearance_help', {
                            defaultValue: 'Set the widget name, placement, and independent theme colors.',
                          })}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <TextField
                              fullWidth
                              name="widget_name"
                              label={translate('widget_setup_page.widget_name', { defaultValue: 'Widget name' })}
                              value={settings.widget_name}
                              onChange={handleInputChange}
                              disabled={brandingDisabled}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              fullWidth
                              name="position"
                              label={translate('widget_setup_page.position', { defaultValue: 'Position' })}
                              value={settings.position}
                              onChange={handleInputChange}
                              disabled={brandingDisabled}
                            >
                              <MenuItem value="bottom_right">
                                {translate('widget_setup_page.bottom_right', { defaultValue: 'Bottom right' })}
                              </MenuItem>
                              <MenuItem value="bottom_left">
                                {translate('widget_setup_page.bottom_left', { defaultValue: 'Bottom left' })}
                              </MenuItem>
                              <MenuItem value="right_middle">
                                {translate('widget_setup_page.right_middle_rectangle', {
                                  defaultValue: 'Right middle rectangle',
                                })}
                              </MenuItem>
                            </TextField>
                          </Grid>
                          {['primary', 'secondary'].map((type) => {
                            const field = `${type}_color`;
                            return (
                              <Grid item xs={12} md={6} key={type}>
                                <TextField
                                  fullWidth
                                  name={field}
                                  label={
                                    type === 'primary'
                                      ? translate('widget_setup_page.primary_color', { defaultValue: 'Primary color' })
                                      : translate('widget_setup_page.secondary_color', {
                                          defaultValue: 'Secondary color',
                                        })
                                  }
                                  value={settings[field]}
                                  onChange={handleColorInput}
                                  disabled={brandingDisabled}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          disabled={brandingDisabled}
                                          onClick={(event) => openColorPicker(event, type)}
                                        >
                                          <ColorizeRoundedIcon />
                                        </IconButton>
                                        <Box
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            bgcolor: settings[field],
                                            border: '1px solid',
                                            borderColor: 'divider',
                                          }}
                                        />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            );
                          })}
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {translate('widget_setup_page.hex_color_help', {
                                defaultValue: 'Enter 6-digit hex colors, for example #ffffff.',
                              })}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Stack direction="row" justifyContent="flex-end">
                        <LoadingButton
                          variant="contained"
                          loading={saveLoader}
                          onClick={saveWidgetSettings}
                          disabled={brandingDisabled}
                          sx={{ minWidth: 150 }}
                        >
                          {translate('widget_setup_page.save_widget', { defaultValue: 'Save Widget' })}
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </Paper>
                </Tooltip>
              )}

              {activeTab === 'setup' && (
                <>
                  <Paper variant="outlined" sx={{ ...sectionPaperSx, ...disabledSectionSx }}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          name="greeting_message"
                          label={translate('widget_setup_page.greeting_message', {
                            defaultValue: 'AI chat greeting message',
                          })}
                          value={settings.greeting_message}
                          onChange={handleInputChange}
                          disabled={setupDisabled}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={translate('widget_setup_page.widget_key', { defaultValue: 'Widget key' })}
                          value={
                            settings.widget_key ||
                            translate('widget_setup_page.generated_on_save', { defaultValue: 'Generated on save' })
                          }
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" justifyContent="flex-end">
                          <LoadingButton
                            variant="contained"
                            loading={saveLoader}
                            onClick={saveWidgetSettings}
                            disabled={setupDisabled}
                            sx={{ minWidth: 150 }}
                          >
                            {translate('widget_setup_page.save_widget', { defaultValue: 'Save Widget' })}
                          </LoadingButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper variant="outlined" sx={{ ...sectionPaperSx, ...disabledSectionSx }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6">
                    {translate('widget_setup_page.website_embed_script', { defaultValue: 'Website embed script' })}
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="pre"
                      aria-label={translate('widget_setup_page.website_embed_script', {
                        defaultValue: 'Website embed script',
                      })}
                      sx={{
                        minHeight: 120,
                        m: 0,
                        p: 2,
                        pr: 6,
                        overflow: 'auto',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'background.default',
                        boxShadow: 'inset 0 1px 2px rgba(33, 43, 54, 0.05)',
                        color: embedScript ? 'text.primary' : 'text.secondary',
                        fontFamily: 'monospace',
                        fontSize: 13,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        userSelect: 'text',
                      }}
                    >
                      {embedScript ||
                        translate('widget_setup_page.save_to_generate_website_script', {
                          defaultValue: 'Save widget setup to generate the website script',
                        })}
                    </Box>
                    <Tooltip title={translate('widget_setup_page.copy_script', { defaultValue: 'Copy script' })}>
                      <Box component="span" sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton
                          size="small"
                          onClick={copyEmbedScript}
                          disabled={setupDisabled || !settings.widget_key}
                          aria-label={translate('widget_setup_page.copy_script', { defaultValue: 'Copy script' })}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.lighter' },
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </Box>
                </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ ...sectionPaperSx, ...disabledSectionSx }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6">
                    {translate('widget_setup_page.hide_show_widget_api', { defaultValue: 'Hide/show widget API' })}
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="pre"
                      aria-label={translate('widget_setup_page.hide_show_widget_api', {
                        defaultValue: 'Hide/show widget API',
                      })}
                      sx={{
                        minHeight: 120,
                        m: 0,
                        p: 2,
                        pr: 6,
                        overflow: 'auto',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'background.default',
                        boxShadow: 'inset 0 1px 2px rgba(33, 43, 54, 0.05)',
                        color: 'text.primary',
                        fontFamily: 'monospace',
                        fontSize: 13,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        userSelect: 'text',
                      }}
                    >
                      {widgetControlExample}
                    </Box>
                    <Tooltip title={translate('widget_setup_page.copy_script', { defaultValue: 'Copy script' })}>
                      <Box component="span" sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton
                          size="small"
                          onClick={copyWidgetControlExample}
                          disabled={setupDisabled}
                          aria-label={translate('widget_setup_page.copy_script', { defaultValue: 'Copy script' })}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.lighter' },
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </Box>
                </Stack>
                  </Paper>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
      <ColorPopover
        popoverElement={colorAnchor}
        selectedPopOverData={selectedColor}
        handleCloseMenu={() => setColorAnchor(null)}
        handleColor={handlePickedColor}
        color={selectedColor === 'primary' ? settings.primary_color : settings.secondary_color}
      />
    </>
  );
}
